import { useState, useEffect, useRef } from "react";

// Undo-pattern modal — action has ALREADY fired when this appears.
// Shows a countdown, "Undo" reverses the action, timer expiry/Done confirms it.
export default function UndoConfirm({ open, title, message, undoSeconds = 5, onConfirm, onCancel }) {
  const [remaining, setRemaining] = useState(undoSeconds);
  const timerRef = useRef(null);
  const settledRef = useRef(false);

  useEffect(() => {
    if (!open) {
      clearInterval(timerRef.current);
      setRemaining(undoSeconds);
      settledRef.current = false;
      return;
    }
    setRemaining(undoSeconds);
    settledRef.current = false;

    timerRef.current = setInterval(() => {
      setRemaining(prev => {
        if (prev <= 1) {
          clearInterval(timerRef.current);
          if (!settledRef.current) {
            settledRef.current = true;
            onConfirm();
          }
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timerRef.current);
  }, [open]);

  if (!open) return null;

  const handleDone = () => {
    if (settledRef.current) return;
    settledRef.current = true;
    clearInterval(timerRef.current);
    onConfirm();
  };

  const handleUndo = () => {
    if (settledRef.current) return;
    settledRef.current = true;
    clearInterval(timerRef.current);
    onCancel();
  };

  const pct = remaining / undoSeconds;
  const r = 14;
  const circ = 2 * Math.PI * r;
  const dash = circ * pct;

  return (
    <div
      onClick={(e) => { if (e.target === e.currentTarget) handleDone(); }}
      style={{ position:"fixed", inset:0, background:"rgba(0,0,0,0.4)", display:"flex", alignItems:"center", justifyContent:"center", zIndex:500, fontFamily:"'DM Sans', system-ui, sans-serif" }}
    >
      <div style={{ background:"#1a1a1a", border:"1px solid #333", borderRadius:12, padding:"24px 26px", width:340, boxShadow:"0 8px 32px rgba(0,0,0,0.4)" }}>
        <div style={{ marginBottom:14 }}>
          <svg width="36" height="36" viewBox="0 0 36 36">
            <circle cx="18" cy="18" r={r} fill="none" stroke="#333" strokeWidth="3" />
            <circle
              cx="18" cy="18" r={r}
              fill="none" stroke="#ef4444" strokeWidth="3"
              strokeDasharray={`${dash} ${circ}`}
              strokeLinecap="round"
              transform="rotate(-90 18 18)"
              style={{ transition:"stroke-dasharray 0.9s linear" }}
            />
            <text x="18" y="23" textAnchor="middle" fontSize="12" fontWeight="700" fill="#fff" fontFamily="inherit">
              {remaining}
            </text>
          </svg>
        </div>

        <div style={{ fontSize:14, fontWeight:700, color:"#fff", marginBottom:6 }}>{title}</div>
        <div style={{ fontSize:12, color:"#aaa", marginBottom:20, lineHeight:1.55 }}>{message}</div>

        <div style={{ display:"flex", gap:8, justifyContent:"flex-end" }}>
          {/* Undo is the prominent action — it's what the user will reach for */}
          <button
            onClick={handleUndo}
            style={{ background:"#3b82f6", border:"none", borderRadius:6, padding:"7px 16px", fontSize:12, fontWeight:600, color:"#fff", cursor:"pointer", fontFamily:"inherit" }}
          >
            Undo
          </button>
          <button
            onClick={handleDone}
            style={{ background:"#2a2a2a", border:"1px solid #444", borderRadius:6, padding:"7px 14px", fontSize:12, color:"#aaa", cursor:"pointer", fontFamily:"inherit" }}
          >
            Done
          </button>
        </div>
      </div>
    </div>
  );
}